import React, { useRef, useState, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SwiperCore, { Autoplay, Keyboard, Mousewheel } from 'swiper';
import { Swiper as SwiperInstance } from 'swiper/react';
import { AutoplayHelper } from '@common/utils/carouselUtils';
import useBreakpoint from '@utils/useBreakpoint';
SwiperCore.use([Keyboard, Autoplay, Mousewheel]);
const swipeTiming = '500ms';
const timingSnap = 200;
const AutoplayCarousel = (props) => {
    const { children, afterContainer, beforeContainer, swiperConf, withArrows, darkMode } = props;
    const { t } = useTranslation();
    const [autoplayHelper, setAutoplayHelper] = useState();
    const [isAutoplayActive, setIsAutoplayActive] = useState(true);
    const [isLocked, setIsLocked] = useState(false);
    const swiperRef = useRef();
    const container = useRef();
    const currentBreakpoint = useBreakpoint();
    const renderAfterContainer = (pauseButton) => {
        return typeof afterContainer === 'function' ? afterContainer(pauseButton) : afterContainer === 'pauseBtn' ? pauseButton : afterContainer;
    };
    const renderBeforeContainer = (pauseButton) => {
        return typeof beforeContainer === 'function' ? beforeContainer(pauseButton) : beforeContainer === 'pauseBtn' ? pauseButton : beforeContainer;
    };
    // useeffect that reInit slider on change of children
    useEffect(() => {
        if (swiperRef.current) {
            swiperRef.current.update();
        }
    }, [swiperConf]);
    const realignPausedCarousel = (originalTransform) => {
        swiperRef.current.previousIndex--;
        swiperRef.current.realIndex--;
        swiperRef.current.activeIndex--;
        const originalTransformValues = originalTransform[1].split(/,\s?/g).map(parseInt);
        const originalDestTranslate = Math.floor(originalTransformValues[0]);
        const normalizedTranslate = Math.floor(Math.abs(originalDestTranslate));
        const normalizedSnapGrid = swiperRef.current.snapGrid.map((val) => Math.floor(Math.abs(val)));
        const originalDestIndex = normalizedSnapGrid.indexOf(normalizedTranslate);
        swiperRef.current.translate = -normalizedSnapGrid[originalDestIndex - 1];
    };
    const pauseCarousel = () => {
        if (!swiperRef.current || !swiperRef.current.autoplay.running) {
            return;
        }
        swiperRef.current.autoplay.stop();
        swiperRef.current.animating = false;
        swiperRef.current.wrapperEl.style.transitionDuration = '0ms';
        const originalTransform = swiperRef.current.wrapperEl.style.transform.split(/\w+\(|\);?/);
        swiperRef.current.wrapperEl.style.transform = getComputedStyle(swiperRef.current.wrapperEl).transform;
        setIsAutoplayActive(false);
        setTimeout(() => {
            swiperRef.current.wrapperEl.style.transitionDuration = swipeTiming;
            swiperRef.current.wrapperEl.style.transform = `translate3d(${swiperRef.current.getTranslate()}px, 0, 0)`;
            const leftmostSlide = swiperRef.current.slides[swiperRef.current.previousIndex];
            const leftmostSlideBounds = leftmostSlide === null || leftmostSlide === void 0 ? void 0 : leftmostSlide.getBoundingClientRect();
            const leftmostHalfSlideVisible = leftmostSlideBounds.x >= 0 || Math.abs(leftmostSlideBounds.left) <= leftmostSlideBounds.width / 2;
            if (leftmostHalfSlideVisible) {
                realignPausedCarousel(originalTransform);
            }
        }, 10);
    };
    const playCarousel = () => {
        if (!swiperRef.current.autoplay.running) {
            swiperRef.current.autoplay.start();
            setIsAutoplayActive(true);
        }
    };
    const handlePauseButton = () => {
        if (isAutoplayActive) {
            setIsLocked(true);
            pauseCarousel();
        }
        else {
            setIsLocked(false);
            playCarousel();
        }
    };
    const nextSlide = () => {
        swiperRef.current.slidePrev(500);
    };
    const prevSlide = () => {
        swiperRef.current.slideNext(500);
    };
    const handleArrowShow = ({ pageX }) => {
        if (!withArrows)
            return;
        const percentage = (pageX / container.current.offsetWidth) * 100;
        if (percentage < 25) {
            container.current.classList.add('show-left-arrow');
            container.current.classList.remove('show-right-arrow');
        }
        else if (percentage > 75) {
            container.current.classList.add('show-right-arrow');
            container.current.classList.remove('show-left-arrow');
        }
        else {
            container.current.classList.remove('show-right-arrow');
            container.current.classList.remove('show-left-arrow');
        }
    };
    const handleArrowHide = () => {
        if (!withArrows)
            return;
        container.current.classList.remove('show-right-arrow');
        container.current.classList.remove('show-left-arrow');
    };
    const playClass = darkMode ? 'btn--pause-white' : 'btn--pause';
    const pauseClass = darkMode ? 'btn--play-white' : 'btn--pause';
    const pauseButton = () => {
        return (React.createElement("button", { onClick: handlePauseButton, className: `autoplay-carousel__play ${isAutoplayActive ? pauseClass : playClass}`, "aria-label": t('button.pause'), "aria-pressed": !isAutoplayActive }));
    };
    const arrowClassName = (direction) => {
        return `icon--chevron-${direction}-big`;
    };
    return (React.createElement("div", { className: "container" },
        renderBeforeContainer(pauseButton()),
        withArrows && (React.createElement("div", { className: "arrows-container", onMouseEnter: () => autoplayHelper === null || autoplayHelper === void 0 ? void 0 : autoplayHelper.pauseCarousel(), onMouseLeave: () => {
                handleArrowHide();
                if (!isAutoplayActive && !isLocked) {
                    autoplayHelper === null || autoplayHelper === void 0 ? void 0 : autoplayHelper.playCarousel();
                }
            } },
            React.createElement("button", { type: "button", className: `arrow--prev`, "aria-label": t('carousel.prevslidemessage'), onClick: () => nextSlide() },
                React.createElement("i", { className: arrowClassName('left'), "aria-hidden": true })),
            React.createElement("button", { type: "button", className: `arrow--next`, "aria-label": t('carousel.nextslidemessage'), onClick: () => prevSlide() },
                React.createElement("i", { className: arrowClassName('right'), "aria-hidden": true })))),
        React.createElement("div", { ref: container, className: "product-recommended__content", onMouseEnter: () => autoplayHelper === null || autoplayHelper === void 0 ? void 0 : autoplayHelper.pauseCarousel(), onMouseMove: (e) => {
                // Prevent mousewheel scrolling both parent and nested carousel
                const eTargetClassList = e.target.classList;
                if (eTargetClassList.contains('product-tile__image') || eTargetClassList.contains('product-tile-carousel__button')) {
                    swiperRef.current.mousewheel.disable();
                }
                else {
                    swiperRef.current.mousewheel.enable();
                }
                handleArrowShow(e);
            }, onMouseLeave: () => {
                handleArrowHide();
                if (!isAutoplayActive && !isLocked) {
                    autoplayHelper === null || autoplayHelper === void 0 ? void 0 : autoplayHelper.playCarousel();
                }
            } },
            React.createElement(Suspense, { fallback: React.createElement("div", null, "Loading...") },
                React.createElement(SwiperInstance, Object.assign({ onSwiper: (swiper) => {
                        swiperRef.current = swiper;
                        setAutoplayHelper(new AutoplayHelper(swiperRef.current, setIsAutoplayActive, isAutoplayActive, setIsLocked));
                    }, onScroll: (swiper, e) => {
                        swiper.wrapperEl.style.transitionDuration = swipeTiming;
                    }, onTouchStart: (swiper) => {
                        swiper.wrapperEl.classList.add('no-transition');
                    }, onTouchEnd: (swiper) => {
                        swiperRef.current.slideReset(timingSnap);
                        setTimeout(() => {
                            swiperRef.current.wrapperEl.classList.remove('no-transition');
                        }, 10);
                    }, 
                    // onTouchEnd={() => {
                    // }}
                    onResize: () => {
                        if (!swiperRef.current.autoplay.running && !isLocked) {
                            playCarousel();
                        }
                    } }, swiperConf), children))),
        renderAfterContainer(pauseButton())));
};
export default AutoplayCarousel;
